import { Technique, TechniqueCategory } from '@/types/soloChoreographer';

export const TECHNIQUES: Technique[] = [
  // ═══════════════════════════════════════════════════════════════
  // SUSTAIN
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'sustained_note',
    name: 'Sustained Note',
    category: 'sustain',
    description: 'Single note held with confidence',
    outputPhrase: 'single sustained note, let it ring and breathe'
  },
  {
    id: 'sustained_bend',
    name: 'Sustained Bend',
    category: 'sustain',
    description: 'Bend up and hold at target pitch',
    outputPhrase: 'sustained bend pushing up, hold at the peak with conviction'
  },
  {
    id: 'feedback_swell',
    name: 'Feedback Swell',
    category: 'sustain',
    description: 'Let the amp feedback bloom',
    outputPhrase: 'controlled feedback swell, amp singing back'
  },
  {
    id: 'harmonic_squeal',
    name: 'Harmonic Squeal',
    category: 'sustain',
    description: 'Pinch harmonic that screams',
    outputPhrase: 'pinch harmonic squeal, overtone screaming'
  },
  {
    id: 'volume_swell',
    name: 'Volume Swell',
    category: 'sustain',
    description: 'Fade in with volume knob',
    outputPhrase: 'volume swell fade-in, note blooms from silence'
  },
  {
    id: 'volume_swell_note',
    name: 'Volume Swell (Note)',
    category: 'sustain',
    description: 'Fade in with volume knob',
    outputPhrase: 'volume swell fade-in, note blooms from silence'
  },

  // ═══════════════════════════════════════════════════════════════
  // MELODIC
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'ascending_run',
    name: 'Ascending Run',
    category: 'melodic',
    description: 'Scale run climbing upward',
    outputPhrase: 'ascending melodic run, climbing through the scale'
  },
  {
    id: 'descending_run',
    name: 'Descending Run',
    category: 'melodic',
    description: 'Scale run falling downward',
    outputPhrase: 'descending run, cascading down gracefully'
  },
  {
    id: 'call_phrase',
    name: 'Call Phrase',
    category: 'melodic',
    description: 'Musical question waiting for response',
    outputPhrase: 'call phrase, melodic question hanging in the air'
  },
  {
    id: 'response_phrase',
    name: 'Response Phrase',
    category: 'melodic',
    description: 'Answer to the previous phrase',
    outputPhrase: 'response phrase, answering the call with resolution'
  },
  {
    id: 'hook_melody',
    name: 'Hook Melody',
    category: 'melodic',
    description: 'Singable, memorable melodic phrase',
    outputPhrase: 'singable hook melody, something you could hum back'
  },
  {
    id: 'arpeggio',
    name: 'Arpeggio',
    category: 'melodic',
    description: 'Chord tones played in sequence',
    outputPhrase: 'arpeggiated flow through the chord tones'
  },
  {
    id: 'arpeggio_climb',
    name: 'Arpeggio Climb',
    category: 'melodic',
    description: 'Chord tones played ascending',
    outputPhrase: 'arpeggiated climb through the chord tones'
  },
  {
    id: 'pentatonic_lick',
    name: 'Pentatonic Lick',
    category: 'melodic',
    description: 'Classic blues/rock pentatonic phrase',
    outputPhrase: 'classic pentatonic lick, bread and butter rock phrasing'
  },

  // ═══════════════════════════════════════════════════════════════
  // RHYTHMIC
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'staccato_punches',
    name: 'Staccato Punches',
    category: 'rhythmic',
    description: 'Short, percussive note hits',
    outputPhrase: 'staccato punches, short and percussive hits'
  },
  {
    id: 'galloping_triplets',
    name: 'Galloping Triplets',
    category: 'rhythmic',
    description: 'Driving triplet feel',
    outputPhrase: 'galloping triplets, driving forward relentlessly'
  },
  {
    id: 'syncopated_hits',
    name: 'Syncopated Hits',
    category: 'rhythmic',
    description: 'Off-beat accent pattern',
    outputPhrase: 'syncopated hits, playing against the beat'
  },
  {
    id: 'tremolo',
    name: 'Tremolo',
    category: 'rhythmic',
    description: 'Rapid repeated single note',
    outputPhrase: 'tremolo articulation, rapid-fire repeated energy'
  },
  {
    id: 'tremolo_picking',
    name: 'Tremolo Picking',
    category: 'rhythmic',
    description: 'Rapid repeated single note',
    outputPhrase: 'tremolo picking, rapid-fire repeated notes'
  },
  {
    id: 'double_stops',
    name: 'Double Stops',
    category: 'rhythmic',
    description: 'Two-note chord stabs',
    outputPhrase: 'double-stop chord stabs, chunky and rhythmic'
  },

  // ═══════════════════════════════════════════════════════════════
  // EXPRESSIVE
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'crying_bend',
    name: 'Crying Bend',
    category: 'expressive',
    description: 'Slow emotional bend with feeling',
    outputPhrase: 'crying bend, slow and emotional, full of feeling'
  },
  {
    id: 'aggressive_bend',
    name: 'Aggressive Bend',
    category: 'expressive',
    description: 'Fast forceful bend with attitude',
    outputPhrase: 'aggressive bend, fast and forceful, attacking the note'
  },
  {
    id: 'slide_into',
    name: 'Slide Into Note',
    category: 'expressive',
    description: 'Slide up into target note',
    outputPhrase: 'sliding up into the note from below'
  },
  {
    id: 'slide_out',
    name: 'Slide Out / Away',
    category: 'expressive',
    description: 'Slide away letting note trail',
    outputPhrase: 'sliding away, letting the note trail off into nothing'
  },
  {
    id: 'hammer_pull_flurry',
    name: 'Legato Flurry',
    category: 'expressive',
    description: 'Hammer-on/pull-off burst',
    outputPhrase: 'flurry of hammer-ons and pull-offs, smooth legato'
  },
  {
    id: 'vibrato_shake',
    name: 'Heavy Vibrato Shake',
    category: 'expressive',
    description: 'Exaggerated vibrato moment',
    outputPhrase: 'heavy vibrato shake, note wobbling with intensity'
  },
  {
    id: 'rake_into_note',
    name: 'Rake Into Note',
    category: 'expressive',
    description: 'Muted string rake leading to note',
    outputPhrase: 'rake across muted strings into the target note'
  },

  // ═══════════════════════════════════════════════════════════════
  // TECHNICAL
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'tapping_sequence',
    name: 'Tapping Sequence',
    category: 'technical',
    description: 'Two-hand tapping pattern',
    outputPhrase: 'two-hand tapping sequence, fingers flying on the fretboard'
  },
  {
    id: 'sweep_arpeggio',
    name: 'Sweep Picking',
    category: 'technical',
    description: 'Fast swept arpeggios',
    outputPhrase: 'sweep-picked arpeggios, fluid and lightning fast'
  },
  {
    id: 'string_skipping',
    name: 'String Skipping',
    category: 'technical',
    description: 'Wide interval string jumps',
    outputPhrase: 'string-skipping pattern, wide angular intervals'
  },
  {
    id: 'speed_burst',
    name: 'Speed Burst',
    category: 'technical',
    description: 'Sudden blazing fast run',
    outputPhrase: 'sudden speed burst, blazing through notes'
  },
  {
    id: 'octave_run',
    name: 'Octave Run',
    category: 'technical',
    description: 'Wes Montgomery style octaves',
    outputPhrase: 'octave run, doubled notes moving in parallel'
  },

  // ═══════════════════════════════════════════════════════════════
  // ATMOSPHERIC
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'ambient_swells',
    name: 'Ambient Swells',
    category: 'atmospheric',
    description: 'Volume swells creating texture',
    outputPhrase: 'ambient volume swells, ethereal and floating'
  },
  {
    id: 'harmonic_chimes',
    name: 'Harmonic Chimes',
    category: 'atmospheric',
    description: 'Natural harmonics ringing',
    outputPhrase: 'natural harmonics chiming, bell-like overtones'
  },
  {
    id: 'textural_noise',
    name: 'Textural Noise',
    category: 'atmospheric',
    description: 'Scrapes, pick slides, chaos',
    outputPhrase: 'textural noise, controlled chaos and string scrapes'
  },
  {
    id: 'silence_space',
    name: 'Silence / Space',
    category: 'atmospheric',
    description: 'Intentional rest, let music breathe',
    outputPhrase: 'intentional silence, letting the space speak'
  },
  {
    id: 'behind_nut_bend',
    name: 'Behind-the-Nut Bend',
    category: 'atmospheric',
    description: 'Eerie pitch bend behind nut',
    outputPhrase: 'behind-the-nut bend, eerie pitch warble'
  }
];

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

export function getTechniquesByCategory(category: TechniqueCategory): Technique[] {
  return TECHNIQUES.filter(t => t.category === category);
}

export function getTechniqueById(id: string): Technique | undefined {
  return TECHNIQUES.find(t => t.id === id);
}

export function getAllTechniquesGrouped(): Record<TechniqueCategory, Technique[]> {
  return {
    sustain: getTechniquesByCategory('sustain'),
    melodic: getTechniquesByCategory('melodic'),
    rhythmic: getTechniquesByCategory('rhythmic'),
    expressive: getTechniquesByCategory('expressive'),
    technical: getTechniquesByCategory('technical'),
    atmospheric: getTechniquesByCategory('atmospheric')
  };
}

export const TECHNIQUE_CATEGORY_LABELS: Record<TechniqueCategory, string> = {
  sustain: '🎵 Sustain',
  melodic: '🎼 Melodic',
  rhythmic: '🥁 Rhythmic',
  expressive: '💫 Expressive',
  technical: '⚡ Technical',
  atmospheric: '🌫️ Atmospheric'
};